import React, { useEffect, useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { X, Search, Plus, Minus, Trash2, GripVertical, Zap, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = {
  code: '',
  name: '',
  description: '',
  image: '',
  qrCode: '',
  itemQtys: {}, // { code: qty }
  itemOrder: [], // preserve insertion order
};

// Convert a flat item_codes array (with duplicates) into { code: qty } + order
function fromFlat(flat) {
  const qtys = {};
  const order = [];
  (flat || []).forEach((c) => {
    if (qtys[c] == null) {
      qtys[c] = 0;
      order.push(c);
    }
    qtys[c] += 1;
  });
  return { itemQtys: qtys, itemOrder: order };
}

// Convert { code: qty } + order back into flat item_codes array
function toFlat(qtys, order) {
  const out = [];
  order.forEach((c) => {
    const n = qtys[c] || 0;
    for (let i = 0; i < n; i++) out.push(c);
  });
  return out;
}

export default function AddKitDialog({ open, onClose, editingKit = null }) {
  const { items, kits, addKit, updateKit, categoryColor } = useApp();
  const isEdit = !!editingKit;
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingKit) {
      const { itemQtys, itemOrder } = fromFlat(editingKit.itemCodes);
      setForm({
        code: editingKit.code,
        name: editingKit.name,
        description: editingKit.description || '',
        image: editingKit.image || '',
        qrCode: editingKit.qrCode || editingKit.code,
        itemQtys,
        itemOrder,
      });
    } else {
      setForm(EMPTY);
    }
    setSearch('');
  }, [editingKit, open]);

  const totalUnits = useMemo(
    () => form.itemOrder.reduce((s, c) => s + (form.itemQtys[c] || 0), 0),
    [form.itemOrder, form.itemQtys],
  );

  // Suggest the next available kit code: first gap in the WC1xxx kit
  // sequence, falling back to max + 1. Kits share the WC prefix with items
  // but live in the 1xxx range (WC1001 … WC1041 today) so item codes never
  // collide with kit codes. WC1001 is treated as the effective floor so it
  // stays in scope as an open slot even when the natural min is higher.
  const suggestedCode = useMemo(() => {
    if (isEdit) return null;
    const PREFIX = 'WC';
    const WIDTH = 4;
    const FLOOR = 1001;
    const nums = kits
      .map((k) => {
        const m = /^WC(\d+)$/.exec(k.code || '');
        if (!m) return null;
        const n = parseInt(m[1], 10);
        return n >= 1000 && n < 10000 ? n : null;
      })
      .filter((n) => n !== null);
    if (nums.length === 0) return `${PREFIX}${String(FLOOR).padStart(WIDTH, '0')}`;
    const used = new Set(nums);
    const min = Math.min(Math.min(...nums), FLOOR);
    const max = Math.max(...nums);
    for (let n = min; n <= max; n++) {
      if (!used.has(n)) return `${PREFIX}${String(n).padStart(WIDTH, '0')}`;
    }
    return `${PREFIX}${String(max + 1).padStart(WIDTH, '0')}`;
  }, [kits, isEdit]);

  const availableItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    const selectedSet = new Set(form.itemOrder);
    return items
      .filter((it) => !selectedSet.has(it.code))
      .filter((it) => {
        if (!q) return true;
        return `${it.code} ${it.name} ${it.category}`.toLowerCase().includes(q);
      })
      .slice(0, 100);
  }, [items, search, form.itemOrder]);

  if (!open) return null;

  const addItem = (code) => {
    setForm((f) => ({
      ...f,
      itemOrder: [...f.itemOrder, code],
      itemQtys: { ...f.itemQtys, [code]: 1 },
    }));
  };
  const removeItem = (code) => {
    setForm((f) => {
      const nextQtys = { ...f.itemQtys };
      delete nextQtys[code];
      return {
        ...f,
        itemOrder: f.itemOrder.filter((c) => c !== code),
        itemQtys: nextQtys,
      };
    });
  };
  const bumpQty = (code, delta) => {
    setForm((f) => {
      const it = items.find((i) => i.code === code);
      const max = it?.totalQty || 99;
      const cur = f.itemQtys[code] || 1;
      const next = Math.max(1, Math.min(max, cur + delta));
      return { ...f, itemQtys: { ...f.itemQtys, [code]: next } };
    });
  };

  const autoFillFromInventory = () => {
    if (form.itemOrder.length === 0) {
      toast.error('Add items first');
      return;
    }
    let bumped = 0;
    let reduced = 0;
    setForm((f) => {
      const nextQtys = { ...f.itemQtys };
      f.itemOrder.forEach((code) => {
        const it = items.find((i) => i.code === code);
        if (!it) return;
        const cur = nextQtys[code] || 1;
        const target = it.totalQty || 1;
        if (target > cur) bumped += 1;
        else if (target < cur) reduced += 1;
        nextQtys[code] = target;
      });
      return { ...f, itemQtys: nextQtys };
    });
    const parts = [];
    if (bumped) parts.push(`${bumped} bumped up`);
    if (reduced) parts.push(`${reduced} reduced to available`);
    if (parts.length === 0) parts.push('already matching inventory');
    toast.success(`Auto-filled · ${parts.join(' · ')}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    const code = form.code.trim().toUpperCase();
    if (!code) return toast.error('Kit code is required');
    if (!form.name.trim()) return toast.error('Kit name is required');
    if (!isEdit && kits.some((k) => k.code === code))
      return toast.error(`Kit code ${code} already exists`);
    if (form.itemOrder.length === 0) return toast.error('Add at least one item');

    const flat = toFlat(form.itemQtys, form.itemOrder);

    setSaving(true);
    try {
      const payload = {
        code,
        name: form.name.trim(),
        description: form.description.trim(),
        image: form.image.trim(),
        qrCode: form.qrCode.trim() || code,
        itemCodes: flat,
      };
      if (isEdit) {
        await updateKit(editingKit.code, payload);
        toast.success(`${form.name} updated`);
      } else {
        await addKit(payload);
        toast.success(`Kit ${form.name} created`);
      }
      onClose();
    } catch (err) {
      toast.error(err.message || 'Could not save kit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="surface rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-900">
          <h2 className="text-base font-semibold text-white">{isEdit ? 'Edit Kit' : 'New Kit'}</h2>
          <button type="button" onClick={onClose} className="text-neutral-500 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
                Kit Code *
              </label>
              <div className="relative">
                <input
                  autoFocus
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="WC1042"
                  className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md pl-3 pr-[88px] py-2 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
                  data-testid="add-kit-code-input"
                />
                {!isEdit && suggestedCode && (
                  <button
                    type="button"
                    onClick={() => {
                      setForm((f) => ({
                        ...f,
                        code: suggestedCode,
                        qrCode: f.qrCode ? f.qrCode : suggestedCode,
                      }));
                      toast.success(`Suggested ${suggestedCode}`);
                    }}
                    title={`Use next available code (${suggestedCode})`}
                    className="absolute inset-y-1 right-1 px-2 rounded text-[10px] tracking-wider uppercase font-semibold bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/20 inline-flex items-center gap-1"
                    data-testid="add-kit-next-gap-btn"
                  >
                    <Sparkles size={11} /> Next
                  </button>
                )}
              </div>
              {!isEdit && suggestedCode && (
                <div className="text-[10px] text-neutral-500 mt-1">
                  Next available:{' '}
                  <span className="font-mono text-amber-300">{suggestedCode}</span>
                </div>
              )}
            </div>
            <div>
              <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
                Kit Name *
              </label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Podcast Kit"
                className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
              Description
            </label>
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What this kit is for, what's inside…"
              className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 resize-none"
            />
          </div>

          <div>
            <label className="block text-[11px] tracking-wider uppercase text-neutral-400 mb-1.5">
              Image URL (optional)
            </label>
            <input
              value={form.image}
              onChange={(e) => setForm({ ...form, image: e.target.value })}
              placeholder="https://…"
              className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
            />
          </div>

          <div className="pt-3 border-t border-neutral-900">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <label className="text-[11px] tracking-wider uppercase text-neutral-400">
                Items in this kit ({form.itemOrder.length}{totalUnits !== form.itemOrder.length ? ` · ${totalUnits} units` : ''})
              </label>
              {form.itemOrder.length > 0 && (
                <button
                  type="button"
                  onClick={autoFillFromInventory}
                  title="Set every item's qty to its full inventory total"
                  className="text-[11px] px-2.5 py-1 rounded-md border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 flex items-center gap-1.5"
                >
                  <Zap size={11} /> Auto-fill from inventory
                </button>
              )}
            </div>

            {form.itemOrder.length > 0 && (
              <div className="surface rounded-md mb-3 max-h-[220px] overflow-auto">
                {form.itemOrder.map((code) => {
                  const it = items.find((i) => i.code === code);
                  if (!it) return null;
                  const qty = form.itemQtys[code] || 1;
                  const max = it.totalQty || 1;
                  return (
                    <div
                      key={code}
                      className="flex items-center gap-2 px-3 py-2 border-b border-neutral-900 last:border-b-0"
                    >
                      <GripVertical size={12} className="text-neutral-700" />
                      <span className="text-[11px] font-mono text-neutral-400 w-16">{it.code}</span>
                      <span className="cat-dot" style={{ background: categoryColor(it.category) }} />
                      <span className="text-sm text-white flex-1 truncate">{it.name}</span>

                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => bumpQty(code, -1)}
                          disabled={qty <= 1}
                          className={`h-6 w-6 rounded border grid place-items-center transition-colors ${
                            qty <= 1
                              ? 'border-neutral-800 text-neutral-700 cursor-not-allowed'
                              : 'border-neutral-700 text-neutral-300 hover:bg-amber-500/10 hover:border-amber-500/50 hover:text-amber-300'
                          }`}
                        >
                          <Minus size={11} />
                        </button>
                        <div className="text-[11px] font-mono text-white min-w-[36px] text-center font-semibold">
                          {qty}<span className="text-neutral-500">/{max}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => bumpQty(code, 1)}
                          disabled={qty >= max}
                          className={`h-6 w-6 rounded border grid place-items-center transition-colors ${
                            qty >= max
                              ? 'border-neutral-800 text-neutral-700 cursor-not-allowed'
                              : 'border-neutral-700 text-neutral-300 hover:bg-amber-500/10 hover:border-amber-500/50 hover:text-amber-300'
                          }`}
                        >
                          <Plus size={11} />
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeItem(code)}
                        className="text-[11px] text-rose-400 hover:bg-rose-500/10 px-1.5 py-0.5 rounded ml-1"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="relative mb-2">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Add items by searching name, code, or category…"
                className="w-full bg-neutral-950 border border-neutral-800 text-white text-sm rounded-md pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/60"
              />
            </div>

            <div className="surface rounded-md max-h-[200px] overflow-auto">
              {availableItems.length === 0 && (
                <div className="p-4 text-center text-xs text-neutral-500">
                  {search ? 'No matches.' : 'All items already added.'}
                </div>
              )}
              {availableItems.map((it) => (
                <button
                  type="button"
                  key={it.code}
                  onClick={() => addItem(it.code)}
                  className="w-full flex items-center gap-2 px-3 py-2 border-b border-neutral-900 last:border-b-0 hover:bg-neutral-900 text-left"
                >
                  <Plus size={11} className="text-amber-400" />
                  <span className="text-[11px] font-mono text-neutral-400 w-16">{it.code}</span>
                  <span className="cat-dot" style={{ background: categoryColor(it.category) }} />
                  <span className="text-sm text-white flex-1 truncate">{it.name}</span>
                  <span className="text-[10px] text-neutral-500">{it.category}</span>
                  {it.totalQty > 1 && (
                    <span className="text-[10px] text-amber-400/70 font-mono">
                      Inventory: {it.totalQty}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-neutral-900 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded-md border border-neutral-800 text-neutral-300 hover:bg-neutral-900"
          >
            Cancel
          </button>
          <button type="submit" disabled={saving} className="btn-amber text-xs px-4 py-1.5 rounded-md disabled:opacity-60">
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Kit'}
          </button>
        </div>
      </form>
    </div>
  );
}
